/**
 * Custom Day / Month / Year three-dropdown renderer for SurveyJS text questions.
 *
 * Registered as the "date-dropdown" renderAs for text questions (emitted by the backend
 * SurveyDateDropdowns model for the Inform "Date" question type, QUESTIONTYPE 13 — PADD-3821).
 *
 * Behaviour (mirrors the legacy advance-repository Inform DATE template):
 * - Renders three <select> boxes: Day (1-31), Month (Jan-Dec), Year (descending, past-only).
 * - No time component.
 * - The Year list runs from maxYear down to minYear (default 1900..current year).
 * - The question value is a single "yyyy-MM-dd" string, so the existing answer-code
 *   harvesting / submission logic in SurveyForm.js needs no change. The value is only set
 *   once all three parts are selected; clearing any part clears the value.
 */
import React from "react";
import { Serializer, RendererFactory } from "survey-core";
import { SurveyQuestionText, ReactQuestionFactory } from "survey-react-ui";
import i18n from "i18next";
import "../styles/DateDropdowns.scss";
// Register the renderAs + year-range properties on the "text" question type so SurveyJS
// preserves them when parsing the form JSON built by the backend.
if (!Serializer.findProperty("text", "renderAs")) {
    Serializer.addProperty("text", {
        name: "renderAs",
        default: "default",
        choices: ["default", "date-dropdown"],
    });
}
if (!Serializer.findProperty("text", "minYear")) {
    Serializer.addProperty("text", { name: "minYear:number", default: 1900 });
}
if (!Serializer.findProperty("text", "maxYear")) {
    Serializer.addProperty("text", { name: "maxYear:number" });
}
// The three Inform answer codes for the Day / Month / Year parts (e.g. 5335 / 5336 / 5337).
// Emitted by the backend SurveyDateDropdowns model so getSurveyData can export them as
// companion keys, letting the server rebuild the three THREEDDLDATE_{key}_0_{code} fields
// Inform expects for a DATE question. Registered so SurveyJS preserves them from the JSON.
if (!Serializer.findProperty("text", "dayCode")) {
    Serializer.addProperty("text", { name: "dayCode:string" });
}
if (!Serializer.findProperty("text", "monthCode")) {
    Serializer.addProperty("text", { name: "monthCode:string" });
}
if (!Serializer.findProperty("text", "yearCode")) {
    Serializer.addProperty("text", { name: "yearCode:string" });
}
const MONTH_KEYS = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
];
const FALLBACK_MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
// Pad a number to two digits ("1" -> "01").
const pad2 = (n) => String(n).padStart(2, "0");
// Parse a "yyyy-MM-dd" string into { year, month, day } strings (empty for missing/zero).
function splitDate(value) {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-");
        const y = Number(year);
        const m = Number(month);
        const d = Number(day);
        return {
            year: y ? String(y) : "",
            month: m ? String(m) : "",
            day: d ? String(d) : "",
        };
    }
    return { year: "", month: "", day: "" };
}
class DateDropdowns extends SurveyQuestionText {
    constructor(props) {
        super(props);
        // The three parts are held in local state as the display source of truth, seeded from
        // any existing question value (pre-populate / back navigation). Only a complete, valid
        // date is ever written back to question.value, so a partial selection never produces a
        // malformed stored/submitted value.
        this.state = { ...(this.state || {}), ...splitDate(props?.question?.value) };
    }
    componentDidMount() {
        if (super.componentDidMount) super.componentDidMount();
        // Re-render when the question's validation errors change so the per-part red state
        // (date-dropdown--error) appears and clears in step with SurveyJS validation. The host
        // form's focus-on-error helper (Register.js focusOnFirstError) relies on that class to
        // find and focus the errored Day/Month/Year select, the same way it focuses the native
        // inputs — see its querySelector, which now includes "select.date-dropdown--error".
        if (this.question) {
            this.question.registerFunctionOnPropertyValueChanged(
                "errors",
                this._onErrorsChanged,
                "__dateDropdownsError"
            );
        }
    }
    _onErrorsChanged = () => {
        this.forceUpdate();
    };
    componentWillUnmount() {
        if (this.question) {
            this.question.unRegisterFunctionOnPropertyValueChanged("errors", "__dateDropdownsError");
        }
        if (super.componentWillUnmount) super.componentWillUnmount();
    }
    // Current { year, month, day } from component state.
    get parts() {
        const { year = "", month = "", day = "" } = this.state || {};
        return { year, month, day };
    }
    // True when SurveyJS has flagged this question invalid (e.g. "Response required").
    // Drives the per-part red styling and which part to focus on error.
    get hasError() {
        const errors = this.question && this.question.errors;
        return Array.isArray(errors) && errors.length > 0;
    }
    // The element id of a date part, matching the ids assigned in renderSelect:
    // Day carries the bare inputId; Month/Year are suffixed.
    partInputId(kind) {
        return kind === "day" ? this.question.inputId : `${this.question.inputId}_${kind}`;
    }
    get maxYear() {
        const configured = this.question.maxYear;
        return configured && configured > 0 ? configured : new Date().getFullYear();
    }
    get minYear() {
        const configured = this.question.minYear;
        return configured && configured > 0 ? configured : 1900;
    }
    // Days in the currently selected month/year (defaults to 31 when month unknown),
    // so invalid combinations like 31 February aren't offered once a month is picked.
    get daysInMonth() {
        const { year, month } = this.parts;
        const m = Number(month);
        if (!m) return 31;
        const y = Number(year) || 2000; // leap-safe default when year not yet chosen
        return new Date(y, m, 0).getDate();
    }
    monthLabel(index) {
        const key = `surveyjs.datedropdowns.months.${MONTH_KEYS[index]}`;
        const translated = i18n.t(key);
        // If no translation is registered, i18next returns the key — fall back to English.
        return translated === key ? FALLBACK_MONTHS[index] : translated;
    }
    label(kind) {
        const key = `surveyjs.datedropdowns.${kind}`;
        const translated = i18n.t(key);
        if (translated !== key) return translated;
        return kind.charAt(0).toUpperCase() + kind.slice(1);
    }
    // Update the picked parts (in state, so the dropdowns keep their selection) and mirror to
    // question.value: a complete date is stored as "yyyy-MM-dd"; anything incomplete clears the
    // value so a partial selection is never stored or submitted.
    commit(next) {
        const parts = { ...this.parts, ...next };
        const { day, month, year } = parts;
        this.setState(parts);
        this.question.value = (day && month && year) ? `${year}-${pad2(month)}-${pad2(day)}` : "";
    }
    onDayChange = (e) => this.commit({ day: e.target.value });
    onMonthChange = (e) => {
        const month = e.target.value;
        // If the selected day no longer fits the new month, drop it.
        const { day } = this.parts;
        const nextDay = day && Number(day) > new Date(Number(this.parts.year) || 2000, Number(month), 0).getDate()
            ? ""
            : day;
        this.commit({ month, day: nextDay });
    };
    onYearChange = (e) => this.commit({ year: e.target.value });
    renderSelect(kind, value, options, onChange, extraClass) {
        // The Day select carries the question's bare inputId so SurveyJS's focus-on-error
        // (survey.focusQuestion → getElementById(question.inputId)) can land on this field,
        // the same as the other fields. Month/Year keep suffixed ids so they stay unique.
        const id = this.partInputId(kind);
        const labelText = this.label(kind);
        const disabled = this.isDisplayMode;
        // "has-value" mirrors SurveyJS's answered state (.sd-dropdown:not(.sd-dropdown--empty)):
        // a filled part gets the blue border, matching the other dropdowns.
        const hasValue = value !== "" && value != null;
        // Per-part error: only the empty part(s) turn red while the question is invalid, so a
        // user who filled Day + Month but not Year sees just the Year box flagged.
        const isErrored = this.hasError && !hasValue;
        return (
            <div className={`date-dropdown-part date-dropdown-${kind}`}>
                <label htmlFor={id} className="offset date-dropdown-label">{labelText}</label>
                <select
                    id={id}
                    className={`date-dropdown ${extraClass}${hasValue ? " has-value" : ""}${isErrored ? " date-dropdown--error" : ""}`}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    aria-label={`${this.question.title} ${labelText}`}
                    aria-required={this.question.isRequired ? "true" : undefined}
                    aria-invalid={isErrored ? "true" : undefined}
                >
                    <option value="">{labelText}</option>
                    {options}
                </select>
            </div>
        );
    }
    renderElement() {
        const { day, month, year } = this.parts;
        const dayOptions = [];
        for (let d = 1; d <= this.daysInMonth; d++) {
            dayOptions.push(<option key={d} value={String(d)}>{d}</option>);
        }
        const monthOptions = FALLBACK_MONTHS.map((_, i) => (
            <option key={i + 1} value={String(i + 1)}>{this.monthLabel(i)}</option>
        ));
        const yearOptions = [];
        for (let y = this.maxYear; y >= this.minYear; y--) {
            yearOptions.push(<option key={y} value={String(y)}>{y}</option>);
        }
        return (
            <div className="date-dropdown" role="group" aria-label={this.question.title}>
                {this.renderSelect("day", day, dayOptions, this.onDayChange, "dateDay date small")}
                {this.renderSelect("month", month, monthOptions, this.onMonthChange, "dateMonth date small")}
                {this.renderSelect("year", year, yearOptions, this.onYearChange, "dateYear date small")}
            </div>
        );
    }
}
// Register the component and route text questions with renderAs "date-dropdown" to it.
ReactQuestionFactory.Instance.registerQuestion("sv-date-dropdown", (props) => {
    return React.createElement(DateDropdowns, props);
});
RendererFactory.Instance.registerRenderer("text", "date-dropdown", "sv-date-dropdown");
export default DateDropdowns;
